import type { Language } from '@eformulieren/engine'
import type { ReactNode } from 'react'
import { deadlineText } from './deadline.js'

/**
 * De deadline, zichtbaar in het formulier zelf en niet alleen in het takenoverzicht van het
 * processysteem (R-128, R-78). Alleen tonen: dat een verstreken deadline het formulier
 * alleen-lezen maakt is een apart gedrag (R-130) en hoort niet bij deze component.
 *
 * Sinds 2 november 2026 met de resterende dagen erbij, "Deadline 1 december 2026, nog 29 dagen", in
 * de metaregel van de kop en niet meer als getinte chip (eisenboek 12.47). De kleur draagt de stand:
 * neutraal, de waarschuwingskleur vanaf zeven dagen ervoor, en de foutkleur als hij verstreken is
 * (`deadline.ts`). De tekst zegt het ook, zodat de kleur nooit de enige drager is (WCAG 1.4.1).
 */
export interface DeadlineBadgeProps {
  /** Een tijdstip in UTC (`Moment` uit `packages/model`), al gevalideerd door de aanroeper. */
  readonly deadline: string
  readonly taal: Language
  /** Nu, van de klok van het formulier (`nu` van `FillForm`), zodat een test de dagen vastzet. */
  readonly nu?: Date
}

export function DeadlineBadge({ deadline, taal: language, nu: now = new Date() }: DeadlineBadgeProps): ReactNode {
  const text = deadlineText(deadline, now, language)
  if (text === undefined) return null
  const rest = text.stand === 'verstreken' ? (language === 'nl' ? ' is verstreken' : ' has passed') : `, ${text.rest}`
  return (
    // `role="status"`, zoals `.ef-reeks__stand` ernaast: de deadline komt pas na de eerste render
    // op (een aparte, niet-fatale fetch), dus zonder statusregio merkt wie niet naar het scherm
    // kijkt niets van een deadline die alsnog verschijnt (R-139, R-140). De resterende dagen
    // veranderen hooguit eens per dag, dus de regio praat niet bij elke aanslag.
    <span className="ef-deadline" data-stand={text.stand} role="status">
      <span className="ef-deadline__label">Deadline</span> {text.datum}
      {rest}
    </span>
  )
}
